<script setup lang="ts">
import { computed, nextTick, ref } from 'vue'

import type { TabsProps, TabValue } from './types'

import { useControllableModel, useId } from '@/shared/ui/composables'

const props = defineProps<TabsProps>()
// An explicit null default prevents Vue's Boolean-prop coercion from turning an
// unbound boolean-capable model into `false`.
const model = defineModel<TabValue | null>({ default: null })
const selected = useControllableModel<TabValue | null>(model, {
  defaultValue: props.defaultValue ?? props.tabs.find((tab) => !tab.disabled)?.value,
})
const rootId = useId('tabs')
const buttons = ref<HTMLButtonElement[]>([])

const enabledIndexes = computed(() =>
  props.tabs.flatMap((tab, index) => (tab.disabled ? [] : [index])),
)

function select(value: TabValue, disabled = false): void {
  if (!disabled) selected.value = value
}

function tabId(index: number): string {
  return `${rootId}-tab-${index}`
}

function panelId(index: number): string {
  return `${rootId}-panel-${index}`
}

async function moveFocus(currentIndex: number, direction: 1 | -1): Promise<void> {
  const available = enabledIndexes.value
  const position = available.indexOf(currentIndex)
  if (position < 0 || available.length === 0) return
  const nextPosition = (position + direction + available.length) % available.length
  const nextIndex = available[nextPosition]
  const nextTab = nextIndex === undefined ? undefined : props.tabs[nextIndex]
  if (nextIndex === undefined || !nextTab) return
  select(nextTab.value)
  await nextTick()
  buttons.value[nextIndex]?.focus()
}
</script>

<template>
  <div class="ui-tabs w-full font-sans">
    <div
      class="ui-tabs__list flex w-full gap-2xs"
      :class="
        variant === 'pill'
          ? 'ui-tabs__list--pill flex-wrap items-center gap-xs'
          : 'items-end justify-center border-b border-border'
      "
      role="tablist"
      :aria-label="ariaLabel"
    >
      <button
        v-for="(tab, index) in tabs"
        :id="tabId(index)"
        :key="String(tab.value)"
        :ref="(element) => element && (buttons[index] = element as HTMLButtonElement)"
        class="ui-tabs__tab cursor-pointer focus-visible:outline-[var(--ui-ring-width)] focus-visible:outline-offset-2 focus-visible:outline-ring disabled:cursor-not-allowed disabled:opacity-50"
        :class="
          variant === 'pill'
            ? {
                'ui-tabs__tab--pill rounded-sm border border-border bg-surface px-md py-2xs text-xs font-semibold text-muted hover:border-info hover:bg-surface-sunken hover:text-fg': true,
                'ui-tabs__tab--active !border-info !bg-info-soft !text-info shadow-xs':
                  selected === tab.value,
              }
            : {
                'min-h-10 min-w-[8.75rem] rounded-t-sm border-0 border-b-2 border-transparent bg-surface px-md text-muted hover:bg-surface-sunken hover:text-fg': true,
                'ui-tabs__tab--active border-b-info bg-info-soft font-semibold text-info':
                  selected === tab.value,
              }
        "
        type="button"
        role="tab"
        :disabled="tab.disabled"
        :aria-selected="selected === tab.value"
        :aria-controls="panelId(index)"
        :tabindex="selected === tab.value ? 0 : -1"
        @click="select(tab.value, tab.disabled)"
        @keydown.right.prevent="moveFocus(index, 1)"
        @keydown.left.prevent="moveFocus(index, -1)"
        @keydown.down.prevent="moveFocus(index, 1)"
        @keydown.up.prevent="moveFocus(index, -1)"
      >
        <slot name="tab" :tab="tab" :selected="selected === tab.value">{{ tab.label }}</slot>
      </button>
      <div v-if="$slots.actions" class="ui-tabs__actions ms-auto flex items-center gap-xs">
        <slot name="actions" />
      </div>
    </div>
    <div
      v-for="(tab, index) in tabs"
      v-show="selected === tab.value"
      :id="panelId(index)"
      :key="`panel-${String(tab.value)}`"
      class="ui-tabs__panel py-md"
      role="tabpanel"
      :aria-labelledby="tabId(index)"
      tabindex="0"
    >
      <slot :name="`panel-${String(tab.value)}`" :tab="tab" />
    </div>
  </div>
</template>
